// context/NotificationContext.jsx
// ─────────────────────────────────────────────────────────────
// Drop-in replacement compatible with your existing pattern:
//   import { useNotification } from "../../context/NotificationContext";
//   const { notify } = useNotification();
//   notify("success", "Saved!");
//   notify("error", "Something went wrong.");
//   notify("info", "Processing...");
//   notify("warning", "Check your settings.");
// ─────────────────────────────────────────────────────────────
import { createContext, useContext, useState, useCallback } from "react";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);

  const notify = useCallback((type, message) => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  }, []);

  const dismiss = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      <NotificationStack notifications={notifications} onDismiss={dismiss} />
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotification must be used inside NotificationProvider");
  return ctx;
}

// ── Notification UI stack ─────────────────────────────────────
const TYPE_CFG = {
  success: { bg:"#f0fdf4", border:"#86efac", text:"#166534", icon:"✓", iconBg:"#16a34a" },
  error:   { bg:"#fef2f2", border:"#fca5a5", text:"#991b1b", icon:"✕", iconBg:"#dc2626" },
  info:    { bg:"#eff6ff", border:"#93c5fd", text:"#1e40af", icon:"ℹ", iconBg:"#2563eb" },
  warning: { bg:"#fffbeb", border:"#fcd34d", text:"#92400e", icon:"⚠", iconBg:"#d97706" },
};

function NotificationStack({ notifications, onDismiss }) {
  if (!notifications.length) return null;
  return (
    <div style={{
      position: "fixed", top: 20, right: 24, zIndex: 99999,
      display: "flex", flexDirection: "column", gap: 10,
      pointerEvents: "none",
    }}>
      {notifications.map(n => {
        const cfg = TYPE_CFG[n.type] || TYPE_CFG.info;
        return (
          <div key={n.id} style={{
            pointerEvents: "all",
            display: "flex", alignItems: "center", gap: 12,
            background: cfg.bg,
            border: `1px solid ${cfg.border}`,
            borderRadius: 10,
            padding: "12px 16px",
            minWidth: 300, maxWidth: 420,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            fontFamily: "'Outfit', sans-serif",
            animation: "dpSlideIn 0.2s ease",
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: "50%",
              background: cfg.iconBg, color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 700, flexShrink: 0,
            }}>
              {cfg.icon}
            </div>
            <span style={{ fontSize: 13, color: cfg.text, fontWeight: 500, flex: 1, lineHeight: 1.4 }}>
              {n.message}
            </span>
            <button onClick={() => onDismiss(n.id)} style={{
              background: "transparent", border: "none", cursor: "pointer",
              color: cfg.text, fontSize: 16, opacity: 0.5, padding: "0 2px", lineHeight: 1,
              flexShrink: 0,
            }}>×</button>
          </div>
        );
      })}
    </div>
  );
}
